/**
 * Real org dashboard — replaces the demo `routes/dashboard.tsx` data source with
 * live metrics from GET /reports/dashboard. The manager should point the /app
 * index route at this component (see manifest).
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarDays,
  HeartHandshake,
  ScanLine,
  TrendingUp,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { ColumnChart } from './charts'
import { getDashboard, type DashboardSummaryDto } from '../../lib/reporting-api'

const METRIC_ICONS: Record<string, LucideIcon> = {
  active_people: Users,
  new_this_month: UserPlus,
  groups: HeartHandshake,
  upcoming_events: CalendarDays,
  recent_checkins: ScanLine,
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'Unscheduled'
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatCheckinTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ReportsDashboard() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: getDashboard,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="A live snapshot of your church community."
        actions={
          <Button variant="secondary" asChild>
            <Link to="/app/reports">View reports</Link>
          </Button>
        }
      />

      {isLoading ? (
        <Card className="flex items-center justify-center gap-2 py-20 text-muted">
          <Spinner /> Loading dashboard…
        </Card>
      ) : isError ? (
        <Card className="space-y-2 py-20 text-center">
          <p className="text-sm text-danger">Couldn’t load the dashboard.</p>
          <p className="text-xs text-dim">
            {error instanceof Error ? error.message : 'Is the API running on :3000?'}
          </p>
        </Card>
      ) : data ? (
        <DashboardContent data={data} />
      ) : null}
    </div>
  )
}

function DashboardContent({ data }: { data: DashboardSummaryDto }) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {data.metrics
          .filter((m) => m.key !== 'recent_checkins')
          .map((m) => {
            const Icon = METRIC_ICONS[m.key] ?? Users
            return (
              <Card key={m.key}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="grid size-10 place-items-center rounded-lg bg-card text-secondary">
                      <Icon className="size-5" />
                    </div>
                    <Badge variant="success">
                      <TrendingUp className="size-3" />
                      Live
                    </Badge>
                  </div>
                  <div className="mt-4 font-display text-3xl font-bold tracking-tight tabular-nums">
                    {m.value.toLocaleString()}
                  </div>
                  <div className="mt-1 text-sm text-muted">{m.label}</div>
                  {m.hint && <div className="mt-3 text-xs text-dim">{m.hint}</div>}
                </CardContent>
              </Card>
            )
          })}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>New people · last 6 months</CardTitle>
          </CardHeader>
          <CardContent>
            <ColumnChart
              data={data.newPeopleByMonth.map((b) => ({
                key: b.bucket,
                label: b.label,
                count: b.count,
              }))}
              emptyLabel="No new people in this window."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Upcoming events</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {data.upcomingEvents.length === 0 ? (
              <p className="text-muted">No upcoming events scheduled.</p>
            ) : (
              data.upcomingEvents.map((e) => (
                <Link
                  key={e.id}
                  to="/app/events"
                  className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2 transition-colors hover:bg-card-hover"
                >
                  <span className="min-w-0 flex-1 truncate text-fg/90">{e.name}</span>
                  <Badge variant="secondary" className="shrink-0">
                    {formatWhen(e.startsAt)}
                  </Badge>
                </Link>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent check-ins</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {data.recentCheckins.length === 0 ? (
            <p className="text-muted">No recent check-ins.</p>
          ) : (
            data.recentCheckins.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-fg/90">
                  {c.personName ?? 'Unknown'}
                </span>
                <span className="shrink-0 text-xs text-dim">
                  {formatCheckinTime(c.checkedInAt)}
                </span>
                <Badge
                  variant={c.status === 'checked_in' ? 'success' : 'neutral'}
                  className="shrink-0 capitalize"
                >
                  {c.status.replace('_', ' ')}
                </Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <p className="text-right text-xs text-dim">
        Generated {new Date(data.generatedAt).toLocaleString()}
      </p>
    </>
  )
}
